import React, { useState, useEffect, useRef } from "react";
import {
  sendSellerChatMessage,
  getSellerChatSuggestions,
  buildChatHistory,
} from "../../services/chatbotService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  contactInfo,
  sellerWelcomeMessage,
} from "../../utils/customerSupportData";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../auth/AuthContext";
import ChatProductCards from "../chatbot/ChatProductCards";

const SellerChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [bottomOffset, setBottomOffset] = useState("1.5rem");
  const messagesEndRef = useRef(null);
  const { setIsChatbotOpen } = useChat();
  const { user, isGuest } = useAuth();
  const isAuthenticated = Boolean(user && !isGuest);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  const handleToggleChat = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    setIsChatbotOpen(newState);
  };

  // Load suggestions lazily on first open — not on every page mount
  const suggestionsLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    suggestionsLoadedRef.current = false;
    const loadSuggestions = async () => {
      try {
        const fetchedSuggestions = await getSellerChatSuggestions();
        setSuggestions(fetchedSuggestions);
        suggestionsLoadedRef.current = true;
      } catch (error) {
        setSuggestions([
          "How do I list an item?",
          "What are the fees?",
          "Tips to get more bookings?",
        ]);
      }
    };
    loadSuggestions();
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    const isMobileView = () => window.matchMedia("(max-width: 767px)").matches;

    const updateBottomOffset = () => {
      if (!isMobileView()) {
        setBottomOffset("1.5rem");
        return;
      }

      const nav = document.querySelector("[data-mobile-bottom-nav]");
      if (!nav) {
        setBottomOffset("1.5rem");
        return;
      }

      const navStyle = window.getComputedStyle(nav);
      if (navStyle.display === "none" || navStyle.visibility === "hidden") {
        setBottomOffset("1.5rem");
        return;
      }

      const isVisible = nav.getAttribute("data-mobile-bottom-nav") === "visible";
      setBottomOffset(isVisible ? "5rem" : "1.5rem");
    };

    updateBottomOffset();
    window.addEventListener("resize", updateBottomOffset);
    window.addEventListener("scroll", updateBottomOffset, { passive: true });
    return () => {
      window.removeEventListener("resize", updateBottomOffset);
      window.removeEventListener("scroll", updateBottomOffset);
    };
  }, []);


  // Initialize with welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          type: "bot",
          text: sellerWelcomeMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen]);

  // Handle sending message
  const handleSend = async (text = inputValue) => {
    if (!text.trim() || isLoading) return;

    // Add user message
    const userMessage = {
      type: "user",
      text: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setShowSuggestions(false);
    setIsLoading(true);
    setIsTyping(true);

    try {
      const history = buildChatHistory(messages);
      const response = await sendSellerChatMessage(text, history);

      const botMessage = {
        type: "bot",
        text: response.response,
        timestamp: new Date(),
        products: response.products || [],
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Chat error:", error);

      // Add error message
      const errorMessage = {
        type: "bot",
        text:
          "I apologize, but I'm having trouble processing your request right now. Please try again or contact our support team at " +
          contactInfo.email,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  // Format timestamp
  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // THEME COLORS (Emerald/Gold/Dark)
  const theme = {
    primary: "from-emerald-600 to-teal-700",
    primaryHover: "hover:from-emerald-700 hover:to-teal-800",
    bgGradient: "from-emerald-50 to-teal-50",
    userMsgBg: "bg-gradient-to-r from-emerald-600 to-teal-700",
    botMsgBg: "bg-white",
    suggestionBg: "bg-emerald-50 text-emerald-700 border-emerald-200",
    suggestionHover: "hover:bg-emerald-100",
    headerGradient: "bg-gradient-to-r from-emerald-800 to-teal-900",
    iconColor: "text-emerald-600",
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={handleToggleChat}
        style={{ bottom: bottomOffset }}
        className={`fixed right-4 sm:right-6 z-50 bg-gradient-to-r ${theme.primary} text-white rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border-2 border-white/20`}
        aria-label="Toggle seller chat"
      >
        {isOpen ? (
          <svg
            className="w-6 h-6 sm:w-7 sm:h-7"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <div className="relative">
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-24 sm:right-6 z-50 sm:w-96 bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border-0 sm:border border-gray-200 sm:min-h-[80vh] sm:max-h-[80vh] md:min-h-[80vh] md:max-h-[80vh] lg:min-h-[80vh] lg:max-h-[80vh] h-full sm:h-auto font-sans">
          {/* Chat Header */}
          <div
            className={`${theme.headerGradient} text-white p-3 sm:p-4 flex items-center justify-between shadow-md`}
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0 backdrop-blur-sm border border-white/20">
                <span className="text-xl">💼</span>
              </div>
              <div>
                <h3 className="font-bold text-base sm:text-lg flex items-center gap-2">
                  Seller Assistant
                  <span className="bg-yellow-400 text-teal-900 text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-bold">
                    Pro
                  </span>
                </h3>
                <p className="text-xs text-emerald-100/80">
                  {isTyping ? "Thinking..." : isAuthenticated ? "Signed in · Seller tools ready" : "Listing help · Online"}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleChat}
              className="text-white/80 hover:bg-white/10 rounded-full p-1 transition-colors flex-shrink-0"
              aria-label="Close chat"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50/50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex flex-col ${
                  message.type === "user" ? "items-end" : "items-start"
                }`}
              >
                <div
                  className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-3 py-2 sm:px-4 shadow-sm ${
                    message.type === "user"
                      ? `${theme.userMsgBg} text-white rounded-br-none`
                      : message.isError
                        ? "bg-red-50 text-red-800 rounded-bl-none border border-red-100"
                        : "bg-white text-gray-800 rounded-bl-none border border-gray-100"
                  }`}
                >
                  <div className="text-xs sm:text-sm leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => (
                          <p className="mb-1 last:mb-0" {...props} />
                        ),
                        ul: ({ node, ...props }) => (
                          <ul
                            className="list-disc ml-4 mb-2 space-y-1"
                            {...props}
                          />
                        ),
                        ol: ({ node, ...props }) => (
                          <ol
                            className="list-decimal ml-4 mb-2 space-y-1"
                            {...props}
                          />
                        ),
                        li: ({ node, ...props }) => (
                          <li className="mb-0.5" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                          <a
                            className="underline hover:text-emerald-200"
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          />
                        ),
                        blockquote: ({ node, ...props }) => (
                          <blockquote
                            className="border-l-4 border-emerald-300 pl-3 italic my-2 bg-emerald-50/50 py-1 rounded-r"
                            {...props}
                          />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong className="font-bold" {...props} />
                        ),
                        code: ({ node, inline, ...props }) =>
                          inline ? (
                            <code
                              className="bg-black/5 rounded px-1 py-0.5 text-xs font-mono"
                              {...props}
                            />
                          ) : (
                            <pre className="bg-black/5 rounded p-2 overflow-x-auto my-1 text-xs font-mono">
                              <code {...props} />
                            </pre>
                          ),
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>

                  <p
                    className={`text-[10px] mt-1 text-right ${
                      message.type === "user"
                        ? "text-emerald-100"
                        : "text-gray-400"
                    }`}
                  >
                    {formatTime(message.timestamp)}
                  </p>
                </div>
                {message.type === "bot" && message.products?.length > 0 && (
                  <div className="max-w-[92%] sm:max-w-[85%] w-full">
                    <ChatProductCards products={message.products} compact />
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex space-x-1.5">
                    <div
                      className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    ></div>
                    <div
                      className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    ></div>
                    <div
                      className="w-1.5 h-1.5 bg-emerald-600 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />

            {showSuggestions && messages.length === 1 && (
              <div className="space-y-3 pt-2">
                <p className="text-xs text-gray-500 font-medium flex items-center gap-1.5 uppercase tracking-wide">
                  <span className="text-emerald-500">⚡</span> Quick Actions
                </p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      disabled={isLoading}
                      className={`${theme.suggestionBg} ${theme.suggestionHover} text-xs px-3 py-2 rounded-lg border transition-all shadow-sm hover:shadow active:scale-95 disabled:opacity-50 text-left`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="p-3 sm:p-4 bg-white border-t border-gray-100">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex items-center space-x-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask about listings, fees, etc..."
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-gray-50 focus:bg-white"
              />
              <button
                type="submit"
                disabled={isLoading || !inputValue.trim()}
                className={`bg-gradient-to-r ${theme.primary} ${theme.primaryHover} text-white rounded-xl w-12 h-12 flex items-center justify-center transition-all shadow-md active:scale-95 disabled:opacity-50 disabled:transform-none`}
                aria-label="Send message"
              >
                {isLoading ? (
                  <svg
                    className="animate-spin w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 transform rotate-90"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                )}
              </button>
            </form>
            <div className="text-center mt-2">
              <p className="text-[10px] text-gray-400">
                Powered by AkkoRent Seller AI
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SellerChatbot;
